import DefaultComparer from '../system/DefaultComparer';
import DefaultEqualityComparer from '../system/DefaultEqualityComparer';
import GeneratorFactory from './GeneratorFactory';
import InvalidOperationError from '../system/InvalidOperationError';
import EnumerableGenerators from './EnumerableGenerators';
import IterableUtils from '../collections/IterableUtils';
export default class Enumerable {
    constructor(iterable) {
        this.iterable = iterable;
    }
    [Symbol.iterator]() {
        return this.iterable[Symbol.iterator]();
    }
    after(predicate) {
        return new Enumerable(new GeneratorFactory(() => EnumerableGenerators.after(this, predicate)));
    }
    afterInclude(predicate) {
        return new Enumerable(new GeneratorFactory(() => EnumerableGenerators.afterInclude(this, predicate)));
    }
    aggregate(seed, func) {
        let accumulated = seed;
        let isFirst = true;
        for (const item of this) {
            if (isFirst) {
                isFirst = false;
                continue;
            }
            accumulated = func(accumulated, item);
        }
        return accumulated;
    }
    all(predicate) {
        for (const item of this.iterable) {
            if (predicate && !predicate(item)) {
                return false;
            }
            else if (!predicate && item === false) {
                return false;
            }
        }
        return true;
    }
    any(predicate) {
        for (const item of this.iterable) {
            if (predicate && predicate(item)) {
                return true;
            }
            else if (!predicate && item === true) {
                return true;
            }
        }
        return false;
    }
    append(value) {
        return new Enumerable(new GeneratorFactory(() => EnumerableGenerators.append(this, value)));
    }
    as() {
        return this;
    }
    average(selector, valueIfNaN = undefined) {
        let length = 0;
        let total = 0;
        for (const item of this.iterable) {
            const value = selector ? selector(item) : item;
            total += !(value === undefined || isNaN(value)) ? value : valueIfNaN;
            length += 1;
        }
        if (length > 0 && total !== undefined && !isNaN(total)) {
            return total / length;
        }
        else {
            return valueIfNaN;
        }
    }
    batch(size) {
        return new Enumerable(new GeneratorFactory(() => EnumerableGenerators.batch(this, size)));
    }
    before(predicate) {
        return new Enumerable(new GeneratorFactory(() => EnumerableGenerators.before(this, predicate)));
    }
    beforeInclude(predicate) {
        return new Enumerable(new GeneratorFactory(() => EnumerableGenerators.beforeInclude(this, predicate)));
    }
    clone() {
        return new Enumerable(new GeneratorFactory(() => EnumerableGenerators.clone(this)));
    }
    combinationsWith(other) {
        return new Enumerable(new GeneratorFactory(() => EnumerableGenerators.combinationsWith(this, other)));
    }
    compare(other) {
        return IterableUtils.compare(this, other);
    }
    contains(value, equalityComparer = DefaultEqualityComparer.equal) {
        for (const item of this.iterable) {
            if (equalityComparer(item, value)) {
                return true;
            }
        }
        return false;
    }
    count(predicate) {
        let count = 0;
        for (const item of this.iterable) {
            if (predicate === undefined || predicate(item)) {
                count++;
            }
        }
        return count;
    }
    distinct(equalityComparer) {
        return new Enumerable(new GeneratorFactory(() => EnumerableGenerators.distinct(this, equalityComparer)));
    }
    distinctConsecutives(equalityComparer, keepLast) {
        return new Enumerable(new GeneratorFactory(() => EnumerableGenerators.distinctConsecutives(this, equalityComparer, keepLast)));
    }
    do(action) {
        return new Enumerable(new GeneratorFactory(() => EnumerableGenerators.do(this, action)));
    }
    evenItems() {
        return new Enumerable(new GeneratorFactory(() => EnumerableGenerators.evenItems(this)));
    }
    except(other, equalityComparer) {
        return new Enumerable(new GeneratorFactory(() => EnumerableGenerators.except(this, other, equalityComparer)));
    }
    exists(predicate) {
        for (const item of this.iterable) {
            if (predicate(item)) {
                return true;
            }
        }
        return false;
    }
    first(predicate) {
        const value = this.firstOrDefault(predicate);
        if (value === undefined) {
            throw new InvalidOperationError('Sequence contains no elements.');
        }
        return value;
    }
    firstOrDefault(predicate, defaultValue) {
        for (const item of this.iterable) {
            if (!predicate || predicate(item)) {
                return item;
            }
        }
        return defaultValue;
    }
    forEach(action) {
        for (const item of this.iterable) {
            action(item);
        }
    }
    groupBy(keySelector, elementSelector, resultSelector) {
        var _a;
        const dict = {};
        for (const item of this.iterable) {
            const key = keySelector(item);
            const elementCollection = dict[key];
            const element = (_a = elementSelector === null || elementSelector === void 0 ? void 0 : elementSelector(item)) !== null && _a !== void 0 ? _a : item;
            if (!elementCollection) {
                dict[key] = [element];
            }
            else {
                elementCollection.push(element);
            }
        }
        const resultDict = {};
        for (const key in dict) {
            if (resultSelector) {
                resultDict[key] = resultSelector(new Enumerable(dict[key]), key);
            }
            else {
                resultDict[key] = new Enumerable(dict[key]);
            }
        }
        return resultDict;
    }
    groupByAsMap(keySelector, elementSelector, resultSelector) {
        var _a;
        const map = new Map();
        for (const item of this.iterable) {
            const key = keySelector(item);
            const elementCollection = map.get(key);
            const element = (_a = elementSelector === null || elementSelector === void 0 ? void 0 : elementSelector(item)) !== null && _a !== void 0 ? _a : item;
            if (!elementCollection) {
                map.set(key, [element]);
            }
            else {
                elementCollection.push(element);
            }
        }
        const resultMap = new Map();
        for (const key of map.keys()) {
            if (resultSelector) {
                resultMap.set(key, resultSelector(new Enumerable(map.get(key)), key));
            }
            else {
                resultMap.set(key, new Enumerable(map.get(key)));
            }
        }
        return resultMap;
    }
    hasOneOf(values) {
        for (const item of this.iterable) {
            for (const value of values) {
                if (item === value) {
                    return true;
                }
            }
        }
        return false;
    }
    intersect(other, equalityComparer) {
        return new Enumerable(new GeneratorFactory(() => EnumerableGenerators.intersect(this, other, equalityComparer)));
    }
    isEmpty() {
        const iterator = this[Symbol.iterator]();
        if (iterator.next().done) {
            return false;
        }
        return true;
    }
    keyBy(keySelector) {
        const dict = {};
        for (const item of this.iterable) {
            const key = keySelector(item);
            if (dict[key]) {
                throw new InvalidOperationError(`Key '${key}' has more than one value.`);
            }
            else {
                dict[key] = item;
            }
        }
        return dict;
    }
    keyByAsMap(keySelector) {
        const map = new Map();
        for (const item of this.iterable) {
            const key = keySelector(item);
            if (map.has(key)) {
                throw new InvalidOperationError(`Key '${key}' contains more than one value.`);
            }
            else {
                map.set(key, item);
            }
        }
        return map;
    }
    max(selector) {
        let maxItem;
        let maxValue;
        for (const item of this.iterable) {
            const value = selector ? selector(item) : item;
            if (value !== undefined && (maxValue === undefined || DefaultComparer.compare(value, maxValue) === 1)) {
                maxItem = item;
                maxValue = value;
            }
        }
        return maxItem;
    }
    min(selector) {
        let minItem;
        let minValue;
        for (const item of this.iterable) {
            const value = selector ? selector(item) : item;
            if (value !== undefined && (minValue === undefined || DefaultComparer.compare(value, minValue) === -1)) {
                minItem = item;
                minValue = value;
            }
        }
        return minValue;
    }
    none(predicate) {
        for (const item of this) {
            if (predicate && predicate(item)) {
                return false;
            }
            else if (!predicate && item === true) {
                return false;
            }
        }
        return true;
    }
    oddItems() {
        return new Enumerable(new GeneratorFactory(() => EnumerableGenerators.oddItems(this)));
    }
    orderBy(keySelector, comparer, order) {
        return new Enumerable(new GeneratorFactory(() => EnumerableGenerators.orderBy(this, keySelector, comparer, order)));
    }
    orderByDescending(keySelector, comparer) {
        return new Enumerable(new GeneratorFactory(() => EnumerableGenerators.orderByDescending(this, keySelector, comparer)));
    }
    orderByLike(keySelector, order, equalityComparer) {
        return new Enumerable(new GeneratorFactory(() => EnumerableGenerators.orderByLike(this, keySelector, order, equalityComparer)));
    }
    select(selector) {
        return new Enumerable(new GeneratorFactory(() => EnumerableGenerators.select(this, selector)));
    }
    selectMany(selector) {
        return new Enumerable(new GeneratorFactory(() => EnumerableGenerators.selectMany(this, selector)));
    }
    sequenceEqual(other, equalityComparer) {
        return IterableUtils.equal(this.iterable, other, equalityComparer);
    }
    single(predicate) {
        let singleItem;
        for (const item of this) {
            if (!predicate || predicate(item)) {
                if (singleItem !== undefined) {
                    throw new InvalidOperationError('Sequence contains more than one element.');
                }
                singleItem = item;
            }
        }
        if (singleItem === undefined) {
            throw new InvalidOperationError('Sequence contains no elements.');
        }
        return singleItem;
    }
    singleOrDefault(predicate, defaultValue) {
        let singleItem;
        for (const item of this) {
            if (!predicate || predicate(item)) {
                if (singleItem !== undefined) {
                    return defaultValue;
                }
                singleItem = item;
            }
        }
        return singleItem !== null && singleItem !== void 0 ? singleItem : defaultValue;
    }
    skip(count) {
        return new Enumerable(new GeneratorFactory(() => EnumerableGenerators.skip(this, count)));
    }
    sorted(order, comparer) {
        return new Enumerable(new GeneratorFactory(() => EnumerableGenerators.sorted(this, order, comparer)));
    }
    sortedLike(order, equalityComparer) {
        return new Enumerable(new GeneratorFactory(() => EnumerableGenerators.sortedLike(this, order, equalityComparer)));
    }
    sum(selector, valueIfNaN = undefined) {
        let total = 0;
        for (const item of this) {
            const value = selector ? selector(item) : Number(item);
            total += !(value === undefined || isNaN(value)) ? value : valueIfNaN;
        }
        if (isNaN(total)) {
            return valueIfNaN;
        }
        else {
            return total;
        }
    }
    take(count) {
        return new Enumerable(new GeneratorFactory(() => EnumerableGenerators.take(this, count)));
    }
    toArray() {
        return Array.from(this.iterable);
    }
    toDictionary(keySelector, valueSelector) {
        const dict = {};
        for (const item of this) {
            const key = keySelector(item);
            const value = valueSelector(item);
            dict[key] = value;
        }
        return dict;
    }
    toMap(keySelector, valueSelector) {
        const map = new Map();
        for (const item of this) {
            const key = keySelector(item);
            const value = valueSelector(item);
            map.set(key, value);
        }
        return map;
    }
    toString() {
        return Array.from(this.iterable).toString();
    }
    union(other, equalityComparer) {
        return new Enumerable(new GeneratorFactory(() => EnumerableGenerators.union(this, other, equalityComparer)));
    }
    when(condition, then) {
        return new Enumerable(new GeneratorFactory(() => EnumerableGenerators.when(this, condition, then)));
    }
    where(predicate) {
        return new Enumerable(new GeneratorFactory(() => EnumerableGenerators.where(this, predicate)));
    }
    whereAnd(...predicates) {
        return new Enumerable(new GeneratorFactory(() => EnumerableGenerators.whereAnd(this, ...predicates)));
    }
    whereIn(selector, values) {
        return new Enumerable(new GeneratorFactory(() => EnumerableGenerators.whereIn(this, selector, values)));
    }
    whereNotIn(selector, values) {
        return new Enumerable(new GeneratorFactory(() => EnumerableGenerators.whereNotIn(this, selector, values)));
    }
    whereNotNull(predicate) {
        return new Enumerable(new GeneratorFactory(() => EnumerableGenerators.whereNotNull(this, predicate)));
    }
    whereNull(predicate) {
        return new Enumerable(new GeneratorFactory(() => EnumerableGenerators.whereNull(this, predicate)));
    }
    whereOr(...predicates) {
        return new Enumerable(new GeneratorFactory(() => EnumerableGenerators.whereOr(this, ...predicates)));
    }
    whereStringEquals(value1Selector, value2Selector, caseSensitive) {
        return new Enumerable(new GeneratorFactory(() => EnumerableGenerators.whereStringEquals(this, value1Selector, value2Selector, caseSensitive)));
    }
}
//# sourceMappingURL=Enumerable.js.map